import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PROVIDER_PATHS } from "@/constants/paths";
import { useSnackbar } from "@/hooks/useSnackbar";
import { GROUP_SESSION_DETAIL_QUERY_KEY } from "@/queries/group-visit/use-group-session-by-id";
import { GROUP_SESSION_LIST_QUERY_KEY } from "@/queries/group-visit/use-group-sessions";
import { useUpdateGroupSessionStatus } from "@/queries/group-visit/use-update-group-session-status";
import { UpdateSessionStatusSessionStatusEnum } from "@/services";
import { getErrorSnackbar, getSuccessSnackbar } from "@/utils/utils";
import { NavigationRunner } from "../group-visit-types";

export type UseSessionStatusActions = {
  cancelSession: () => Promise<void>;
  isSubmitting: boolean;
};

/**
 * Cancel and Complete for the session being documented. Both close the
 * session and return to the Group Appointments list, which shows the result.
 */
export const useSessionStatusActions = (
  sessionId?: string,
  runNavigation: NavigationRunner = (navigationFn) => navigationFn()
): UseSessionStatusActions => {
  const client = useQueryClient();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { mutateAsync: updateSessionStatus, isPending } =
    useUpdateGroupSessionStatus();

  const changeStatus = useCallback(
    async (sessionStatus: UpdateSessionStatusSessionStatusEnum) => {
      if (!sessionId) return;
      try {
        const res = await updateSessionStatus({ sessionId, sessionStatus });
        showSnackbar(getSuccessSnackbar(res));
        client.invalidateQueries({
          queryKey: [GROUP_SESSION_DETAIL_QUERY_KEY, sessionId],
        });
        client.invalidateQueries({ queryKey: [GROUP_SESSION_LIST_QUERY_KEY] });
        runNavigation(() => navigate(PROVIDER_PATHS.GROUP_APPOINTMENTS));
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    [
      sessionId,
      updateSessionStatus,
      showSnackbar,
      client,
      navigate,
      runNavigation,
    ]
  );

  const cancelSession = useCallback(
    () => changeStatus(UpdateSessionStatusSessionStatusEnum.Cancelled),
    [changeStatus]
  );

  // Completing now goes through the shared note sign-off, which closes the
  // session itself — see `use-session-sign-off`.
  // const completeSession = useCallback(
  //   () => changeStatus(UpdateSessionStatusSessionStatusEnum.Completed),
  //   [changeStatus]
  // );

  return { cancelSession, isSubmitting: isPending };
};
